// Bare authenticated fetch for the chat mini app's single API surface: the
// weebee chat ticket-view endpoint. No Firebase / App Check — the signed view
// token IS the credential, passed as the `token` query param (the endpoint is
// App Check- and Firebase-auth-exempt; the token is verified in the handler).
import { config } from './config';
import type { ConfirmResult, PaySummary, TicketView } from './types';

/** A non-2xx from the ticket-view endpoint. `status` drives the error copy. */
export class ChatApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ChatApiError';
    this.status = status;
  }
}

/**
 * fetchTicketView loads the current, gated ticket view for a signed view token.
 * Throws ChatApiError on a non-2xx (401 = invalid/expired, 503 = unavailable).
 */
export async function fetchTicketView(token: string, signal?: AbortSignal): Promise<TicketView> {
  const url = `${config.apiUrl}/api/v1/chat/ticket?token=${encodeURIComponent(token)}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!res.ok) {
    throw new ChatApiError(res.status, `ticket view failed (${res.status})`);
  }
  return (await res.json()) as TicketView;
}

/**
 * fetchPaySummary loads the side-effect-free booking summary for a pay token —
 * the Razorpay order the checkout opens against. 401 = invalid/expired.
 */
export async function fetchPaySummary(token: string, signal?: AbortSignal): Promise<PaySummary> {
  const url = `${config.apiUrl}/api/v1/chat/pay?token=${encodeURIComponent(token)}`;
  const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  if (!res.ok) {
    throw new ChatApiError(res.status, `pay summary failed (${res.status})`);
  }
  return (await res.json()) as PaySummary;
}

/**
 * pollPayStatus asks whether the paid ticket has been issued yet (the ONDC
 * on_confirm is async). Side-effect-free — it does NOT consume the pay token — so
 * the pay page can poll it after a "processing" confirm and auto-advance to the
 * live QR once `viewToken` is present, instead of sending the rider to WhatsApp.
 */
export async function pollPayStatus(token: string, signal?: AbortSignal): Promise<ConfirmResult> {
  const url = `${config.apiUrl}/api/v1/chat/pay/status?token=${encodeURIComponent(token)}`;
  const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  if (!res.ok) {
    throw new ChatApiError(res.status, `pay status failed (${res.status})`);
  }
  return (await res.json()) as ConfirmResult;
}

/**
 * confirmPay posts the Razorpay proof to finalize the booking. One-time: a
 * 409 means the token was already used or the quote expired.
 */
export async function confirmPay(
  token: string,
  razorpayPaymentId: string,
  razorpaySignature: string
): Promise<ConfirmResult> {
  const url = `${config.apiUrl}/api/v1/chat/pay/confirm?token=${encodeURIComponent(token)}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ razorpayPaymentId, razorpaySignature }),
  });
  if (!res.ok) {
    throw new ChatApiError(res.status, `pay confirm failed (${res.status})`);
  }
  return (await res.json()) as ConfirmResult;
}
